import React, { useState, useRef, useEffect } from 'react';
import { ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { sensorFullNameKey } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import styles from './SensorMultiSelect.module.scss';

export type SensorMultiSelectProps = {
    /** Array of available sensor types to select from */
    sensors: SensorTypes[];
    /** Array of currently selected sensor types */
    selectedSensors: SensorTypes[];
    /** Callback function called when selection changes */
    onSelectionChange: (sensors: SensorTypes[]) => void;
    /** Whether the component is disabled */
    disabled?: boolean;
};

/**
 * SensorMultiSelect Component
 *
 * A reusable multi-select dropdown component for selecting sensors.
 * Displays selected sensors as chips with remove buttons and provides a dropdown
 * list for selecting/deselecting sensors.
 *
 * @example
 * ```tsx
 * const [selectedSensors, setSelectedSensors] = useState<SensorTypes[]>([]);
 *
 * <SensorMultiSelect
 *   sensors={[SensorTypes.co2, SensorTypes.temp, SensorTypes.humidity]}
 *   selectedSensors={selectedSensors}
 *   onSelectionChange={setSelectedSensors}
 *   disabled={false}
 * />
 * ```
 */
const SensorMultiSelect: React.FC<SensorMultiSelectProps> = ({
    sensors,
    selectedSensors,
    onSelectionChange,
    disabled = false,
}) => {
    const { t: txt } = useTranslation();
    const [open, setOpen] = useState(false);
    const [tempSelectedSensors, setTempSelectedSensors] = useState<SensorTypes[]>(selectedSensors);
    const anchorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTempSelectedSensors(selectedSensors);
    }, [selectedSensors]);

    const toggleSensor = (sensor: SensorTypes): void => {
        const isSelected = tempSelectedSensors.includes(sensor);
        if (isSelected) {
            setTempSelectedSensors(tempSelectedSensors.filter(s => s !== sensor));
        } else {
            setTempSelectedSensors([...tempSelectedSensors, sensor]);
        }
    };

    const handleClickAway = (): void => {
        onSelectionChange(tempSelectedSensors);
        setOpen(false);
    };

    const handleToggle = (): void => {
        if (!disabled) {
            setOpen(prev => !prev);
        }
    };

    const availableSensors = sensors.filter(s => s !== SensorTypes.mold);

    const handleRemoveSensor = (sensor: SensorTypes, e: React.MouseEvent): void => {
        e.stopPropagation(); // Prevent opening the dropdown
        const newSelection = tempSelectedSensors.filter(s => s !== sensor);
        setTempSelectedSensors(newSelection);
        onSelectionChange(newSelection);
    };

    const handleSelectAll = (): void => {
        setTempSelectedSensors([...availableSensors]);
    };

    const handleClear = (): void => {
        setTempSelectedSensors([]);
    };
    const hasNoChips = tempSelectedSensors.length === 0;
    let placeholderWhenNoChips: string | null = null;
    if (hasNoChips && !disabled) {
        placeholderWhenNoChips = txt('IAQReport2.NoSensorsSelected');
    }

    const noChipsContent = placeholderWhenNoChips ? (
        <span className={styles.placeholderText}>{placeholderWhenNoChips}</span>
    ) : (
        <span className={styles.emptyPlaceholder}>&nbsp;</span>
    );

    return (
        <div className={styles.sensorMultiSelect}>
            <div
                ref={anchorRef}
                className={`${styles.inputField} ${disabled ? styles.disabled : ''}`}
                onClick={handleToggle}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e): void => {
                    if (disabled) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                <div className={styles.chipsContainer}>
                    {tempSelectedSensors.length > 0
                        ? tempSelectedSensors.map(sensor => (
                              <span key={sensor} className={styles.chip}>
                                  <span className={styles.chipText}>{txt(sensorFullNameKey(sensor))}</span>
                                  <button
                                      type="button"
                                      className={styles.chipRemoveButton}
                                      onClick={(e): void => handleRemoveSensor(sensor, e)}
                                      aria-label={`Remove ${txt(sensorFullNameKey(sensor))}`}
                                  >
                                      <MaterialIcon name="close" extraClass={styles.chipRemoveIcon} />
                                  </button>
                              </span>
                          ))
                        : noChipsContent}
                </div>
            </div>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
            >
                {({ TransitionProps }): React.ReactElement => (
                    <Grow {...TransitionProps}>
                        <Paper className={styles.dropdown}>
                            <ClickAwayListener onClickAway={handleClickAway}>
                                <div className={styles.dropdownContent}>
                                    <div className={styles.actionBar}>
                                        <button type="button" className={styles.actionButton} onClick={handleSelectAll}>
                                            {txt('SelectAll')}
                                        </button>
                                        <button type="button" className={styles.actionButton} onClick={handleClear}>
                                            {txt('ClearAll')}
                                        </button>
                                    </div>
                                    {availableSensors.map(sensor => {
                                        const isSelected = tempSelectedSensors.includes(sensor);
                                        return (
                                            <div
                                                key={sensor}
                                                className={`${styles.option} ${
                                                    isSelected ? styles.optionSelected : ''
                                                }`}
                                                onClick={(): void => toggleSensor(sensor)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e): void => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        toggleSensor(sensor);
                                                    }
                                                }}
                                            >
                                                <span className={styles.optionIconSlot}>
                                                    {isSelected && (
                                                        <MaterialIcon
                                                            name="check"
                                                            extraClass={styles.optionCheckIcon}
                                                        />
                                                    )}
                                                </span>
                                                <span className={styles.optionText}>
                                                    {txt(sensorFullNameKey(sensor))}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
};

export default SensorMultiSelect;
